/* eslint-disable */
/**
 * Validation utilities for Risk Calculator
 * Shared validation logic for product rows and dose inputs
 */

/**
 * Check if a product row is complete (has all required fields)
 *
 * @param {Object} row - Product row object
 * @param {string} row.id - Product ID
 * @param {string|number} row.dose - Applied dose
 * @param {string} row.unit - Unit ID
 * @returns {boolean} True if row has all required fields with valid values
 */
export function isRowComplete(row) {
  return (
    row.id &&
    row.id !== '' &&
    row.dose &&
    row.dose !== '' &&
    parseFloat(row.dose) > 0 &&
    row.unit &&
    row.unit !== ''
  );
}

/**
 * Check if a product row is empty (no product selected)
 *
 * @param {Object} row - Product row object
 * @param {string} row.id - Product ID
 * @returns {boolean} True if row has no product ID
 */
export function isRowEmpty(row) {
  return !row.id || row.id === '';
}

/**
 * Filter out empty rows (for API submission)
 * Only returns rows that have a product selected
 *
 * @param {Array} rows - Array of product row objects
 * @returns {Array} Filtered array with only valid rows
 */
export function filterValidRows(rows) {
  return rows.filter((row) => row.id && row.id !== '');
}

/**
 * Sanitize dose input value
 * - Allows numbers, comma, dot, and minus sign
 * - Replaces dot with comma for display
 * - Only allows one decimal separator
 * - Minus only allowed at start
 *
 * @param {string} inputValue - Raw input value from user
 * @returns {Object} { displayValue: string, storedValue: string }
 */
export function sanitizeDoseInput(inputValue) {
  // Sanitize input (allow numbers, comma, dot, minus)
  let sanitized = inputValue.replace(/[^0-9,.\-]/g, '');

  // Allow minus only at start
  const hasMinus = sanitized.startsWith('-');
  sanitized = sanitized.replace(/-/g, '');
  if (hasMinus) sanitized = '-' + sanitized;

  // Replace dot with comma for display
  sanitized = sanitized.replace(/\./g, ',');

  // Only allow one comma
  const parts = sanitized.split(',');
  if (parts.length > 2) {
    sanitized = parts[0] + ',' + parts.slice(1).join('');
  }

  // Convert comma to dot for storage
  const storedValue = sanitized.replace(',', '.');

  return {
    displayValue: sanitized,
    storedValue,
  };
}

/**
 * Format dose for display (replace dot with comma)
 * French locale uses comma as decimal separator
 *
 * @param {string|number} dose - Dose value with dot as decimal separator
 * @returns {string} Formatted dose with comma as decimal separator
 */
export function formatDoseDisplay(dose) {
  if (!dose || dose === '') return '';
  return String(dose).replace('.', ',');
}

/**
 * Validate that all previous steps are complete
 * Used for step navigation validation
 *
 * @param {Object} selections - Current form selections
 * @param {number} targetStep - Step user is trying to access
 * @returns {Object} { isValid: boolean, maxAccessibleStep: number }
 */
export function validateStepAccess(selections, targetStep) {
  const STEPS = {
    CULTURES: 1,
    TREATMENT_TYPE: 2,
    APPLICATION_TECHNIQUE: 3,
    COMMERCIAL_PRODUCTS: 4,
  };

  let maxAccessibleStep = STEPS.CULTURES;

  // Step 1 valid if cultures selected
  if (selections.cultures && selections.cultures.length > 0) {
    maxAccessibleStep = STEPS.TREATMENT_TYPE;
  }

  // Step 2 valid if treatment type selected
  if (maxAccessibleStep >= STEPS.TREATMENT_TYPE && selections.treatmentType) {
    maxAccessibleStep = STEPS.APPLICATION_TECHNIQUE;
  }

  // Step 3 valid if application technique selected
  if (
    maxAccessibleStep >= STEPS.APPLICATION_TECHNIQUE &&
    selections.applicationTechnique
  ) {
    maxAccessibleStep = STEPS.COMMERCIAL_PRODUCTS;
  }

  return {
    isValid: targetStep <= maxAccessibleStep,
    maxAccessibleStep,
  };
}
